import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { GenericDetailPage } from '../../components/generic';
import { useEmailAccount, EmailAccount } from './useData';
import { moduleConfig, getDetailSections, getStatusBadges } from './config';
import api from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { Button } from '../../components/ui/Button';
import SyncLogsSection from './components/SyncLogsSection';
import ShareAccountButton from './components/ShareAccountButton';
import { SendIdentitiesSection } from './components/SendIdentitiesSection';
import { SentFolderIdentitiesSection } from './components/SentFolderIdentitiesSection';
import { AccountSharesSection } from './components/AccountSharesSection';
import OAuthConnectSection from './components/OAuthConnectSection';

const Detail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: account, mutate } = useEmailAccount(id ? parseInt(id) : 0);
  const { showSuccess, showError } = useNotification();
  const [isSyncing, setIsSyncing] = useState(false);

  const handleRefreshImapFolders = async () => {
    if (!id) return;
    setIsSyncing(true);
    try {
      await api.post(`/emails/accounts/${id}/imap-folders/refresh`);
      showSuccess('IMAP folder list refreshed from server.');
      mutate();
    } catch (error: any) {
      showError(error?.response?.data?.message || 'Failed to refresh folder list');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleFullMirrorSync = async () => {
    if (!id) return;
    setIsSyncing(true);
    try {
      await api.post(`/emails/accounts/${id}/sync?fullMirror=true`);
      showSuccess('Full mailbox sync triggered. Folders will process in sequence.');
      mutate();
    } catch (error: any) {
      showError(error?.response?.data?.message || 'Failed to trigger sync');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSync = async (sinceYears?: number) => {
    if (!id) return;

    setIsSyncing(true);
    try {
      const params = sinceYears != null ? `?sinceYears=${sinceYears}` : '';
      await api.post(`/emails/accounts/${id}/sync${params}`);
      showSuccess(
        sinceYears != null
          ? `Full sync (${sinceYears} years) triggered. Emails will appear in batches.`
          : 'Email sync triggered successfully. Emails will appear shortly.',
      );
      mutate();
    } catch (error: any) {
      console.error('Error triggering sync:', error);
      showError(error?.response?.data?.message || 'Failed to trigger email sync');
    } finally {
      setIsSyncing(false);
    }
  };

  // Order: Shared with, Send identities, Sent/My sent, OAuth2, Sync status, Sync logs, then config sections
  const getDetailSectionsWithSync = (item: EmailAccount, formatDate: (dateString?: string) => string) => {
    const sections = getDetailSections(item, formatDate);

    const syncSectionIndex = sections.findIndex((s) => s.title === 'Sync Status');
    let syncSection = syncSectionIndex >= 0 ? sections[syncSectionIndex] : null;
    const otherSections = sections.filter((_, i) => i !== syncSectionIndex);

    if (syncSection) {
      syncSection = {
        ...syncSection,
        fields: [
          {
            label: 'Actions',
            value: (
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <Button
                  onClick={() => handleSync()}
                  disabled={isSyncing}
                  variant="default"
                >
                  {isSyncing ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Syncing...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Sync Now
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => handleSync(8)}
                  disabled={isSyncing}
                  variant="outline"
                  title="Download all inbox messages from the last 8 years"
                >
                  <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Full sync (8 years)
                </Button>
                {item.incomingProtocol === 'imap' && (
                  <>
                    <Button onClick={() => handleRefreshImapFolders()} disabled={isSyncing} variant="outline">
                      Refresh folder list
                    </Button>
                    <Button
                      onClick={() => handleFullMirrorSync()}
                      disabled={isSyncing}
                      variant="outline"
                      title="Sync all selectable IMAP folders (not only INBOX)"
                    >
                      Sync full mailbox
                    </Button>
                  </>
                )}
              </div>
            ),
          },
          ...syncSection.fields,
        ],
      };
    }

    const sharesSection = {
      title: 'Shared with',
      fields: [
        {
          label: '',
          value: <AccountSharesSection account={item} onMutate={mutate} />,
        },
      ],
    };

    const identitiesSection = {
      title: 'Send identities',
      fields: [
        {
          label: '',
          value: (
            <SendIdentitiesSection
              accountId={item.id}
              accountEmail={item.emailAddress || ''}
              onMutate={mutate}
            />
          ),
        },
      ],
    };

    const sentFolderSection = {
      title: 'Sent / My sent — From addresses',
      fields: [
        {
          label: '',
          value: (
            <SentFolderIdentitiesSection
              accountId={item.id}
              accountEmail={item.emailAddress || ''}
              sentFolderIdentityIds={item.sentFolderIdentityIds}
              onMutate={mutate}
            />
          ),
        },
      ],
    };

    const oauthSection = {
      title: 'OAuth2',
      fields: [
        {
          label: '',
          value: (
            <OAuthConnectSection
              accountId={item.id}
              oauthProvider={item.oauthProvider}
              onConnected={() => mutate()}
            />
          ),
        },
      ],
    };

    const logsSection = {
      title: 'Sync Logs',
      fields: [
        {
          label: '',
          value: <SyncLogsSection accountId={item.id} />,
        },
      ],
    };

    return [
      sharesSection,
      identitiesSection,
      sentFolderSection,
      oauthSection,
      ...(syncSection ? [syncSection] : []),
      logsSection,
      ...otherSections,
    ];
  };

  return (
    <GenericDetailPage<EmailAccount>
      useData={useEmailAccount}
      config={{
        ...moduleConfig,
        detail: {
          ...moduleConfig.detail,
          getExtraHeaderActions: (item) => (
            <ShareAccountButton account={item} onShared={() => mutate()} showLabel />
          ),
        },
        getDetailSections: getDetailSectionsWithSync,
        getStatusBadges,
      }}
    />
  );
};

export default Detail;
